import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useOrganisation } from '../contexts/OrganisationContext';
import { Group, GroupMember, GroupProject, OrganisationMember, Project, ProjectRole } from '../types/api';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '../constants/messages';
import {
  UserGroupIcon, TrashIcon, ArrowPathIcon, FolderIcon, PlusIcon, XMarkIcon, KeyIcon,
} from '@heroicons/react/24/outline';

const ROLES: ProjectRole[] = ['user', 'developer', 'admin'];

// Fiche d'un groupe : deux volets.
// - Membres : qui est dans le groupe, ajout par un modal (cases a cocher parmi
//   les membres de l'organisation), retrait par ligne.
// - Permissions : les projets sur lesquels le groupe donne un role, ajout par
//   un modal (projet + role explique), changement de role en ligne, retrait.
// Un groupe `entra` a sa composition en lecture seule (409 cote API) ; ses
// permissions restent gerees ici.
const OrganisationGroupDetailPage: React.FC = () => {
  const { t } = (useTranslation as any)('organisations');
  const navigate = useNavigate();
  const { orgId, groupId } = useParams<{ orgId: string; groupId: string }>();
  const { currentOrganisation, selectOrganisation, canManageMembers } = useOrganisation();

  const [group, setGroup] = useState<Group | null>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [projects, setProjects] = useState<GroupProject[]>([]);
  const [orgMembers, setOrgMembers] = useState<OrganisationMember[]>([]);
  const [orgProjects, setOrgProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({ nom: '', description: '' });

  const [showMembersModal, setShowMembersModal] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [memberSearch, setMemberSearch] = useState('');

  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [permissionProject, setPermissionProject] = useState('');
  const [permissionRole, setPermissionRole] = useState<ProjectRole>('developer');

  const canManage = canManageMembers();
  const isSynced = group?.source === 'entra';

  useEffect(() => {
    if (orgId && (!currentOrganisation || currentOrganisation.id !== orgId)) {
      selectOrganisation(orgId).catch(() => {
        toast.error(t('groups.loadOrgError'));
        navigate('/organisations');
      });
    }
  }, [orgId, currentOrganisation, selectOrganisation, navigate]);

  const load = useCallback(async () => {
    if (!currentOrganisation || !groupId) return;
    try {
      setLoading(true);
      const [g, m, p] = await Promise.all([
        apiService.getGroup(groupId),
        apiService.getGroupMembers(groupId),
        apiService.getGroupProjects(groupId),
      ]);
      setGroup(g);
      setMembers(m);
      setProjects(p);
      setEditForm({ nom: g.nom, description: g.description ?? '' });
      // Candidats des deux modals : membres et projets de l'organisation
      const [om, op] = await Promise.all([
        apiService.getOrganisationMembers(currentOrganisation.id).catch(() => []),
        apiService.getProjects(currentOrganisation.id).catch(() => []),
      ]);
      setOrgMembers(om);
      setOrgProjects(op);
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setNotFound(true);
      } else {
        toast.error(t('groups.loadError'));
      }
    } finally {
      setLoading(false);
    }
  }, [currentOrganisation, groupId]);

  useEffect(() => {
    if (currentOrganisation) load();
  }, [currentOrganisation, load]);

  const refreshGroup = async () => {
    if (!groupId) return;
    setGroup(await apiService.getGroup(groupId));
  };

  // ------------------------------------------------------------ en-tete

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!group) return;
    const nom = editForm.nom.trim();
    if (!nom) {
      toast.error(t('groups.createModal.nameRequired'));
      return;
    }
    setSaving(true);
    try {
      const updated = await apiService.updateGroup(group.id, {
        ...(isSynced ? {} : { nom }),
        description: editForm.description.trim() || null,
      });
      setGroup(updated);
      setEditing(false);
      toast.success(t('groups.detail.saveSuccess'));
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      toast.error(status === 409 ? t('groups.createModal.nameTaken') : getApiErrorMessage(error, t('groups.detail.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!group) return;
    if (!window.confirm(t('groups.detail.confirmDelete', { name: group.nom }) as string)) return;
    setBusy(true);
    try {
      await apiService.deleteGroup(group.id);
      toast.success(t('groups.detail.deleteSuccess'));
      navigate(`/organisations/${orgId}/groups`);
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, t('groups.detail.deleteError')));
    } finally {
      setBusy(false);
    }
  };

  // ------------------------------------------------------------ membres

  const memberIds = new Set(members.map(m => m.utilisateur_id));
  const memberCandidates = orgMembers.filter(m => !memberIds.has(m.utilisateur_id));
  const visibleCandidates = memberCandidates.filter(m => {
    const q = memberSearch.trim().toLowerCase();
    return !q || `${m.prenom} ${m.nom} ${m.email}`.toLowerCase().includes(q);
  });

  const openMembersModal = () => {
    setSelectedMemberIds([]);
    setMemberSearch('');
    setShowMembersModal(true);
  };

  const toggleSelected = (id: string) => {
    setSelectedMemberIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const handleAddMembers = async () => {
    if (!group || selectedMemberIds.length === 0) return;
    setBusy(true);
    let added = 0;
    try {
      for (const userId of selectedMemberIds) {
        const m = await apiService.addGroupMember(group.id, userId);
        setMembers(prev => (prev.some(x => x.utilisateur_id === m.utilisateur_id) ? prev : [...prev, m]));
        added += 1;
      }
      toast.success(t('groups.detail.membersAdded', { count: added }));
      setShowMembersModal(false);
      await refreshGroup();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, t('groups.detail.memberAddError')));
      if (added > 0) await refreshGroup();
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveMember = async (member: GroupMember) => {
    if (!group) return;
    if (!window.confirm(t('groups.detail.confirmRemoveMember', { name: `${member.prenom} ${member.nom}` }) as string)) return;
    setBusy(true);
    try {
      await apiService.removeGroupMember(group.id, member.utilisateur_id);
      setMembers(prev => prev.filter(m => m.utilisateur_id !== member.utilisateur_id));
      toast.success(t('groups.detail.memberRemoved'));
      await refreshGroup();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, t('groups.detail.memberRemoveError')));
    } finally {
      setBusy(false);
    }
  };

  // ------------------------------------------------------------ permissions

  const grantedIds = new Set(projects.map(p => p.projet_id));
  const projectCandidates = orgProjects.filter(p => !grantedIds.has(p.id));

  const openPermissionModal = () => {
    setPermissionProject(projectCandidates[0]?.id ?? '');
    setPermissionRole('developer');
    setShowPermissionModal(true);
  };

  const handleGrant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!group || !permissionProject) return;
    setBusy(true);
    try {
      const granted = await apiService.grantGroupProject(group.id, permissionProject, permissionRole);
      setProjects(prev => [...prev.filter(p => p.projet_id !== granted.projet_id), granted]);
      toast.success(t('groups.detail.permissionAdded', { project: granted.projet_nom }));
      setShowPermissionModal(false);
      await refreshGroup();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, t('groups.detail.grantError')));
    } finally {
      setBusy(false);
    }
  };

  const handleChangeRole = async (projectId: string, role: ProjectRole) => {
    if (!group) return;
    setBusy(true);
    try {
      const updated = await apiService.updateGroupProject(group.id, projectId, role);
      setProjects(prev => prev.map(p => (p.projet_id === projectId ? updated : p)));
      toast.success(t('groups.detail.roleUpdated'));
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, t('groups.detail.grantError')));
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (gp: GroupProject) => {
    if (!group) return;
    if (!window.confirm(t('groups.detail.confirmRevoke', { project: gp.projet_nom }) as string)) return;
    setBusy(true);
    try {
      await apiService.revokeGroupProject(group.id, gp.projet_id);
      setProjects(prev => prev.filter(p => p.projet_id !== gp.projet_id));
      toast.success(t('groups.detail.permissionRemoved'));
      await refreshGroup();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, t('groups.detail.revokeError')));
    } finally {
      setBusy(false);
    }
  };

  const roleBadge = (role: ProjectRole) => {
    switch (role) {
      case 'admin':
        return 'bg-red-500/12 text-red-400';
      case 'developer':
        return 'bg-blue-500/12 text-blue-400';
      default:
        return 'bg-surface-3 text-text-secondary';
    }
  };

  const modalShell = (onClose: () => void, children: React.ReactNode) => (
    <div className="fixed z-10 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={onClose}></div>
        <div className="inline-block align-bottom bg-surface-2 rounded-card px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button onClick={onClose} className="bg-surface-2 rounded-md text-text-quaternary hover:text-text-tertiary focus:outline-none">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );

  if (loading || !currentOrganisation) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500 mx-auto"></div>
          <p className="mt-4 text-text-secondary">{t('groups.detail.loading')}</p>
        </div>
      </div>
    );
  }

  if (notFound || !group) {
    return (
      <div className="py-8">
        <button onClick={() => navigate(`/organisations/${orgId}/groups`)} className="text-sm text-accent-400 hover:text-accent-300 mb-2">
          ← {t('groups.detail.backToGroups')}
        </button>
        <p className="mt-4 text-text-secondary">{t('groups.detail.notFound')}</p>
      </div>
    );
  }

  return (
    <>
      <div className="py-8">
        {/* En-tete */}
        <div className="mb-6">
          <button onClick={() => navigate(`/organisations/${orgId}/groups`)} className="text-sm text-accent-400 hover:text-accent-300 mb-2">
            ← {t('groups.detail.backToGroups')}
          </button>
          {editing ? (
            <form onSubmit={handleSave} className="bg-surface-2 shadow-card rounded-card p-6 space-y-4 max-w-2xl">
              <div>
                <label htmlFor="edit-name" className="block text-sm font-medium text-text-secondary">{t('groups.createModal.nameLabel')}</label>
                <input
                  id="edit-name"
                  type="text"
                  value={editForm.nom}
                  maxLength={100}
                  disabled={isSynced}
                  onChange={(e) => setEditForm({ ...editForm, nom: e.target.value })}
                  className="mt-1 block w-full border border-border-1 rounded-md shadow-sm py-2 px-3 bg-surface-3 text-text-primary focus:outline-none focus:ring-accent-500 focus:border-accent-500 disabled:opacity-50"
                  required
                />
              </div>
              <div>
                <label htmlFor="edit-description" className="block text-sm font-medium text-text-secondary">{t('groups.createModal.descriptionLabel')}</label>
                <textarea
                  id="edit-description"
                  value={editForm.description}
                  maxLength={500}
                  rows={2}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="mt-1 block w-full border border-border-1 rounded-md shadow-sm py-2 px-3 bg-surface-3 text-text-primary focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                />
              </div>
              <div className="flex space-x-3">
                <button type="submit" disabled={saving} className="px-4 py-2 rounded-md text-sm font-medium text-white bg-accent-600 hover:bg-accent-700 disabled:opacity-50">
                  {saving ? t('groups.detail.saving') : t('groups.detail.save')}
                </button>
                <button type="button" onClick={() => { setEditing(false); setEditForm({ nom: group.nom, description: group.description ?? '' }); }} className="px-4 py-2 rounded-md text-sm font-medium border border-border-1 bg-surface-3 text-text-secondary">
                  {t('cancel', { ns: 'common' })}
                </button>
              </div>
            </form>
          ) : (
            <div className="flex justify-between items-start">
              <div className="min-w-0">
                <h1 className="text-3xl font-bold text-text-primary flex items-center">
                  <UserGroupIcon className="h-7 w-7 mr-3 text-text-quaternary flex-shrink-0" />
                  <span className="truncate">{group.nom}</span>
                  {isSynced && (
                    <span className="ml-3 inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-purple-500/12 text-purple-400 flex-shrink-0">
                      <ArrowPathIcon className="h-3 w-3 mr-1" />
                      {t('groups.source.entra')}
                    </span>
                  )}
                </h1>
                <p className="mt-2 text-text-secondary">
                  {currentOrganisation.nom} · {t('groups.members', { count: group.member_count })} · {t('groups.detail.permissionsCount', { count: group.project_count })}
                </p>
                {group.description && (
                  <p className="mt-1 text-sm text-text-tertiary">{group.description}</p>
                )}
              </div>
              {canManage && (
                <div className="flex space-x-2 flex-shrink-0 ml-4">
                  <button onClick={() => setEditing(true)} className="px-3 py-2 rounded-md text-sm font-medium border border-border-1 bg-surface-3 text-text-secondary hover:bg-surface-2">
                    {t('groups.detail.edit')}
                  </button>
                  {!isSynced && (
                    <button onClick={handleDelete} disabled={busy} className="px-3 py-2 rounded-md text-sm font-medium text-red-400 border border-red-500/30 hover:bg-red-500/10 flex items-center disabled:opacity-50">
                      <TrashIcon className="h-4 w-4 mr-1" />
                      {t('groups.detail.delete')}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {isSynced && (
          <div className="mb-6 p-4 rounded-card bg-purple-500/10 border border-purple-500/20 text-sm text-purple-300">
            {t('groups.detail.readOnlyBanner')}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ---------------- Membres */}
          <div className="bg-surface-2 shadow-card rounded-card overflow-hidden">
            <div className="px-6 py-4 border-b border-border-1 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-text-primary flex items-center">
                  <UserGroupIcon className="h-5 w-5 mr-2 text-text-quaternary" />
                  {t('groups.detail.membersTitle')} ({members.length})
                </h2>
                <p className="mt-1 text-xs text-text-tertiary">{t('groups.detail.membersHint')}</p>
              </div>
              {canManage && !isSynced && (
                <button
                  onClick={openMembersModal}
                  className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-white bg-accent-600 hover:bg-accent-700 flex-shrink-0 ml-4"
                >
                  <PlusIcon className="h-4 w-4 mr-1.5" />
                  {t('groups.detail.addMembers')}
                </button>
              )}
            </div>

            <div className="divide-y divide-border-1">
              {members.map((member) => (
                <div key={member.utilisateur_id} className="px-6 py-3 flex items-center justify-between hover:bg-surface-3">
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className="h-9 w-9 rounded-full bg-blue-500/12 flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-400 text-sm font-medium">{member.prenom.charAt(0)}{member.nom.charAt(0)}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-text-primary truncate">{member.prenom} {member.nom}</p>
                      <p className="text-xs text-text-tertiary truncate">{member.email}</p>
                      <p className="text-xs text-text-quaternary">{t('groups.detail.addedOn', { date: new Date(member.date_ajout).toLocaleDateString() })}</p>
                    </div>
                  </div>
                  {canManage && !isSynced && (
                    <button
                      onClick={() => handleRemoveMember(member)}
                      disabled={busy}
                      className="text-sm text-red-400 hover:text-red-300 flex items-center disabled:opacity-50 flex-shrink-0 ml-4"
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      {t('groups.detail.removeMember')}
                    </button>
                  )}
                </div>
              ))}
              {members.length === 0 && (
                <div className="px-6 py-10 text-center">
                  <UserGroupIcon className="mx-auto h-10 w-10 text-text-quaternary" />
                  <p className="mt-2 text-sm text-text-secondary">{t('groups.detail.membersEmpty')}</p>
                  <p className="mt-1 text-xs text-text-tertiary">
                    {canManage && !isSynced ? t('groups.detail.membersEmptyHint') : t('groups.detail.membersEmptyHintReadOnly')}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* ---------------- Permissions */}
          <div className="bg-surface-2 shadow-card rounded-card overflow-hidden">
            <div className="px-6 py-4 border-b border-border-1 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-text-primary flex items-center">
                  <KeyIcon className="h-5 w-5 mr-2 text-text-quaternary" />
                  {t('groups.detail.permissionsTitle')} ({projects.length})
                </h2>
                <p className="mt-1 text-xs text-text-tertiary">{t('groups.detail.permissionsHint')}</p>
              </div>
              {canManage && (
                <button
                  onClick={openPermissionModal}
                  className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-white bg-accent-600 hover:bg-accent-700 flex-shrink-0 ml-4"
                >
                  <PlusIcon className="h-4 w-4 mr-1.5" />
                  {t('groups.detail.addPermission')}
                </button>
              )}
            </div>

            <div className="divide-y divide-border-1">
              {projects.map((gp) => (
                <div key={gp.projet_id} className="px-6 py-3 flex items-center justify-between hover:bg-surface-3">
                  <div className="flex items-center space-x-3 min-w-0">
                    <FolderIcon className="h-5 w-5 text-text-quaternary flex-shrink-0" />
                    <div className="min-w-0">
                      <button onClick={() => navigate(`/projects/${gp.projet_id}`)} className="text-sm font-medium text-text-primary hover:text-accent-400 text-left truncate">
                        {gp.projet_nom}
                      </button>
                      <p className="text-xs text-text-tertiary">{t(`groups.roleDescriptions.${gp.role_projet}`)}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 flex-shrink-0 ml-4">
                    {canManage ? (
                      <select
                        value={gp.role_projet}
                        disabled={busy}
                        aria-label={t('groups.detail.roleLabel') as string}
                        onChange={(e) => handleChangeRole(gp.projet_id, e.target.value as ProjectRole)}
                        className="text-sm bg-surface-3 border border-border-1 text-text-primary rounded-md py-1 px-2 focus:ring-accent-500 focus:border-accent-500"
                      >
                        {ROLES.map(r => <option key={r} value={r}>{t(`groups.roles.${r}`)}</option>)}
                      </select>
                    ) : (
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${roleBadge(gp.role_projet)}`}>
                        {t(`groups.roles.${gp.role_projet}`)}
                      </span>
                    )}
                    {canManage && (
                      <button
                        onClick={() => handleRevoke(gp)}
                        disabled={busy}
                        title={t('groups.detail.revoke') as string}
                        className="text-red-400 hover:text-red-300 disabled:opacity-50"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {projects.length === 0 && (
                <div className="px-6 py-10 text-center">
                  <KeyIcon className="mx-auto h-10 w-10 text-text-quaternary" />
                  <p className="mt-2 text-sm text-text-secondary">{t('groups.detail.permissionsEmpty')}</p>
                  <p className="mt-1 text-xs text-text-tertiary">
                    {canManage ? t('groups.detail.permissionsEmptyHint') : t('groups.detail.permissionsEmptyHintReadOnly')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal : ajouter des membres */}
      {showMembersModal && modalShell(() => setShowMembersModal(false), (
        <div>
          <h3 className="text-lg leading-6 font-medium text-text-primary">{t('groups.detail.membersModal.title')}</h3>
          <p className="mt-1 text-sm text-text-tertiary">{t('groups.detail.membersModal.hint')}</p>
          {memberCandidates.length === 0 ? (
            <p className="mt-6 text-sm text-text-secondary">{t('groups.detail.membersModal.noCandidates')}</p>
          ) : (
            <>
              <input
                type="search"
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                placeholder={t('groups.detail.membersModal.searchPlaceholder') as string}
                className="mt-4 block w-full border border-border-1 rounded-md py-2 px-3 bg-surface-3 text-text-primary text-sm focus:outline-none focus:ring-accent-500 focus:border-accent-500"
              />
              <div className="mt-3 max-h-72 overflow-y-auto divide-y divide-border-1 border border-border-1 rounded-md">
                {visibleCandidates.map((m) => {
                  const checked = selectedMemberIds.includes(m.utilisateur_id);
                  return (
                    <label key={m.utilisateur_id} className={`flex items-center px-3 py-2.5 cursor-pointer ${checked ? 'bg-accent-500/8' : 'hover:bg-surface-3'}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSelected(m.utilisateur_id)}
                        className="h-4 w-4 rounded border-border-1 text-accent-600 focus:ring-accent-500"
                      />
                      <div className="ml-3 min-w-0">
                        <p className="text-sm font-medium text-text-primary truncate">{m.prenom} {m.nom}</p>
                        <p className="text-xs text-text-tertiary truncate">{m.email}</p>
                      </div>
                    </label>
                  );
                })}
                {visibleCandidates.length === 0 && (
                  <p className="px-3 py-4 text-sm text-text-tertiary">{t('groups.detail.membersModal.noMatch')}</p>
                )}
              </div>
            </>
          )}
          <div className="mt-5 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleAddMembers}
              disabled={busy || selectedMemberIds.length === 0}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-accent-600 text-base font-medium text-white hover:bg-accent-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
            >
              {busy ? t('groups.detail.membersModal.adding') : t('groups.detail.membersModal.submit', { count: selectedMemberIds.length })}
            </button>
            <button
              type="button"
              onClick={() => setShowMembersModal(false)}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-border-1 shadow-sm px-4 py-2 bg-surface-3 text-base font-medium text-text-secondary hover:bg-surface-3 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm"
            >
              {t('cancel', { ns: 'common' })}
            </button>
          </div>
        </div>
      ))}

      {/* Modal : ajouter une permission */}
      {showPermissionModal && modalShell(() => setShowPermissionModal(false), (
        <form onSubmit={handleGrant}>
          <h3 className="text-lg leading-6 font-medium text-text-primary">{t('groups.detail.permissionModal.title')}</h3>
          <p className="mt-1 text-sm text-text-tertiary">{t('groups.detail.permissionModal.hint')}</p>
          {projectCandidates.length === 0 ? (
            <p className="mt-6 text-sm text-text-secondary">{t('groups.detail.permissionModal.noCandidates')}</p>
          ) : (
            <>
              <div className="mt-4">
                <label htmlFor="permission-project" className="block text-sm font-medium text-text-secondary">{t('groups.detail.permissionModal.projectLabel')}</label>
                <select
                  id="permission-project"
                  value={permissionProject}
                  onChange={(e) => setPermissionProject(e.target.value)}
                  className="mt-1 block w-full border border-border-1 rounded-md py-2 px-3 bg-surface-3 text-text-primary text-sm focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                  required
                >
                  {projectCandidates.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
                </select>
              </div>
              <fieldset className="mt-4">
                <legend className="block text-sm font-medium text-text-secondary">{t('groups.detail.permissionModal.roleLabel')}</legend>
                <div className="mt-2 space-y-2">
                  {ROLES.map((r) => (
                    <label key={r} className={`flex items-start p-3 rounded-md border cursor-pointer ${permissionRole === r ? 'border-accent-500 bg-accent-500/8' : 'border-border-1 hover:bg-surface-3'}`}>
                      <input
                        type="radio"
                        name="permission-role"
                        value={r}
                        checked={permissionRole === r}
                        onChange={() => setPermissionRole(r)}
                        className="mt-0.5 h-4 w-4 border-border-1 text-accent-600 focus:ring-accent-500"
                      />
                      <div className="ml-3">
                        <p className="text-sm font-medium text-text-primary">{t(`groups.roles.${r}`)}</p>
                        <p className="text-xs text-text-tertiary">{t(`groups.roleDescriptions.${r}`)}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </fieldset>
            </>
          )}
          <div className="mt-5 sm:flex sm:flex-row-reverse">
            <button
              type="submit"
              disabled={busy || !permissionProject}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-accent-600 text-base font-medium text-white hover:bg-accent-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
            >
              {busy ? t('groups.detail.permissionModal.adding') : t('groups.detail.permissionModal.submit')}
            </button>
            <button
              type="button"
              onClick={() => setShowPermissionModal(false)}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-border-1 shadow-sm px-4 py-2 bg-surface-3 text-base font-medium text-text-secondary hover:bg-surface-3 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm"
            >
              {t('cancel', { ns: 'common' })}
            </button>
          </div>
        </form>
      ))}
    </>
  );
};

export default OrganisationGroupDetailPage;
